import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../providers/firebase/firebase';
import type { NotificationSettingsDoc } from '../../types/notifications';
import type { AlertSeverity } from '../../pages/markets/api/types';

const DEFAULTS: NotificationSettingsDoc = {
    min_severity: 'info',
    enabled_types: null,
    fcm_tokens: {},
    muted_gate_accounts: [],
};

interface SettingsPatch {
    min_severity?: AlertSeverity;
    enabled_types?: string[] | null;
}

interface UseNotificationSettingsResult {
    settings: NotificationSettingsDoc;
    loading: boolean;
    save: (patch: SettingsPatch) => Promise<void>;
}

export function useNotificationSettings(): UseNotificationSettingsResult {
    const [uid, setUid] = useState<string | null>(null);
    const [settings, setSettings] = useState<NotificationSettingsDoc>(DEFAULTS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const auth = getAuth();
        if (auth.currentUser) setUid(auth.currentUser.uid);
        return onAuthStateChanged(auth, u => setUid(u?.uid ?? null));
    }, []);

    useEffect(() => {
        if (!uid) return;
        const ref = doc(db, `users/${uid}/notification_settings/main`);
        const unsub = onSnapshot(ref, snap => {
            setSettings(snap.exists() ? { ...DEFAULTS, ...(snap.data() as NotificationSettingsDoc) } : DEFAULTS);
            setLoading(false);
        });
        return () => unsub();
    }, [uid]);

    const save = useCallback(async (patch: SettingsPatch) => {
        if (!uid) return;
        // Write only the patched fields with merge — never spread the in-memory
        // `settings`, which can hold a stale `fcm_tokens` map (managed solely by
        // useFcmToken) and would clobber a concurrent token add/remove.
        await setDoc(doc(db, `users/${uid}/notification_settings/main`), patch, { merge: true });
    }, [uid]);

    return { settings, loading, save };
}
